import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftCouponStatisticsDataAtom,
  redemptionDashboardDataAtom,
  redemptionDashboardStatusAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  DeliverCouponDto,
  deliverCoupon,
  rejectCoupon,
  updateRecipientPhone,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  giftCouponStatisticsUrl,
  redemptionDashboardUrl,
  useGiftCouponStatisticsHook,
  useRedemptionDashboardHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForRedemption,
  GiftCouponForRedemptionDashboard,
  PhoneHistoryItem,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useQueryClient } from 'react-query';

import { getChoiceUnitPriceRegular } from '../utils/redemption.utils';

export interface DeliverModalState {
  couponIds: string[];
  priceRetail: number;
  priceWhole: string;
  priceRebate: string;
  isBulk: boolean;
}

export interface HistoryModalState {
  phone: string;
  history: PhoneHistoryItem[];
}

export interface ShareHistoryModalState {
  fullCode: string;
  history: Array<{ sharedAt: Date; unsharedAt?: Date }>;
}

export function useRedemptionPage() {
  useRedemptionDashboardHook();
  useGiftCouponStatisticsHook();

  const queryClient = useQueryClient();
  const dashboardStatus = useRecoilValue(redemptionDashboardStatusAtom);
  const dashboard = useRecoilValue(redemptionDashboardDataAtom);
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  const visibleRequested = useMemo(() => {
    const list = dashboard?.requested ?? [];
    return [...list].sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [dashboard]);

  const todayDelivered = useMemo(() => {
    const list = dashboard?.todayDelivered ?? [];
    return [...list].sort((a, b) => {
      const dateA = a.gifticon?.deliveredAt
        ? new Date(a.gifticon.deliveredAt).getTime()
        : 0;
      const dateB = b.gifticon?.deliveredAt
        ? new Date(b.gifticon.deliveredAt).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [dashboard]);

  const invalidateDashboard = useCallback(() => {
    queryClient.invalidateQueries(redemptionDashboardUrl);
    queryClient.invalidateQueries(giftCouponStatisticsUrl);
  }, [queryClient]);

  // Filters & selection
  const [productFilter, setProductFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [deliveringId, setDeliveringId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showTodayDelivered, setShowTodayDelivered] = useState(false);

  // Modals
  const [deliverModal, setDeliverModal] = useState<DeliverModalState | null>(null);
  const [historyModal, setHistoryModal] = useState<HistoryModalState | null>(null);
  const [shareHistoryModal, setShareHistoryModal] =
    useState<ShareHistoryModalState | null>(null);

  // Toast
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(msg);
    toastTimer.current = setTimeout(() => setToastMessage(null), 1200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // Product filter options (with duplicate count & oldest request time)
  const productOptions = useMemo(() => {
    const optionMap = new Map<
      string,
      {
        key: string;
        label: string;
        count: number;
        duplicateCount: number;
        oldestRequestedAt: number;
        requestedAtSum: number;
        requestedAtCount: number;
      }
    >();

    for (const coupon of visibleRequested) {
      let key: string;
      let label: string;

      if (
        coupon.selectedChoiceUnit &&
        typeof coupon.selectedChoiceUnit === 'object' &&
        coupon.selectedChoiceUnit._id
      ) {
        key = `unit:${coupon.selectedChoiceUnit._id}`;
        const itemName =
          typeof coupon.giftItem === 'object'
            ? coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || ''
            : '';
        label = itemName
          ? `${itemName} - ${coupon.selectedChoiceUnit.name}`
          : coupon.selectedChoiceUnit.name;
      } else if (typeof coupon.giftItem === 'object' && coupon.giftItem?._id) {
        key = `item:${coupon.giftItem._id}`;
        label = coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
      } else {
        continue;
      }

      const isDuplicate = !!(coupon as GiftCouponForRedemptionDashboard).isDuplicatePhone;
      const requestedAt = coupon.recipient?.requestedAt
        ? new Date(coupon.recipient.requestedAt).getTime()
        : Infinity;
      const hasValidTime = isFinite(requestedAt);

      const existing = optionMap.get(key);
      if (existing) {
        existing.count++;
        if (isDuplicate) existing.duplicateCount++;
        if (requestedAt < existing.oldestRequestedAt) {
          existing.oldestRequestedAt = requestedAt;
        }
        if (hasValidTime) {
          existing.requestedAtSum += requestedAt;
          existing.requestedAtCount++;
        }
      } else {
        optionMap.set(key, {
          key,
          label,
          count: 1,
          duplicateCount: isDuplicate ? 1 : 0,
          oldestRequestedAt: requestedAt,
          requestedAtSum: hasValidTime ? requestedAt : 0,
          requestedAtCount: hasValidTime ? 1 : 0,
        });
      }
    }

    return Array.from(optionMap.values())
      .map((v) => ({
        key: v.key,
        label: v.label,
        count: v.count,
        duplicateCount: v.duplicateCount,
        oldestRequestedAt: v.oldestRequestedAt,
        avgRequestedAt:
          v.requestedAtCount > 0
            ? Math.round(v.requestedAtSum / v.requestedAtCount)
            : Infinity,
      }))
      .sort((a, b) => a.oldestRequestedAt - b.oldestRequestedAt);
  }, [visibleRequested]);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = [...visibleRequested];

    if (productFilter !== 'all') {
      result = result.filter((item) => {
        if (productFilter.startsWith('unit:')) {
          const unitId = productFilter.slice(5);
          return (
            item.selectedChoiceUnit &&
            typeof item.selectedChoiceUnit === 'object' &&
            item.selectedChoiceUnit._id === unitId
          );
        } else if (productFilter.startsWith('item:')) {
          const itemId = productFilter.slice(5);
          return typeof item.giftItem === 'object' && item.giftItem?._id === itemId;
        }
        return true;
      });
    }

    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const itemName =
          typeof item.giftItem === 'object'
            ? (
                item.giftItem?.name ||
                item.giftItem?.giftItemInfo?.name ||
                ''
              ).toLowerCase()
            : '';
        const unitName =
          item.selectedChoiceUnit && typeof item.selectedChoiceUnit === 'object'
            ? item.selectedChoiceUnit.name?.toLowerCase() || ''
            : '';
        const fullCode = (item.fullCode || '').toLowerCase();
        const order = typeof item.giftOrder === 'object' ? item.giftOrder : null;
        const ordererPhone = (order?.bugo?.member?.phoneNumber || '').replace(/-/g, '');
        return (
          phone.includes(search) ||
          itemName.includes(search) ||
          unitName.includes(search) ||
          fullCode.includes(search) ||
          ordererPhone.includes(search)
        );
      });
    }

    result.sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    return result;
  }, [visibleRequested, productFilter, searchText]);

  // Selection
  const isAllSelected = useMemo(
    () =>
      filteredData.length > 0 && filteredData.every((item) => selectedIds.has(item._id)),
    [filteredData, selectedIds],
  );

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map((item) => item._id)));
    }
  }, [isAllSelected, filteredData]);

  const handleToggleSelect = useCallback((couponId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(couponId)) {
        newSet.delete(couponId);
      } else {
        newSet.add(couponId);
      }
      return newSet;
    });
  }, []);

  // Deliver modal
  const openDeliverModal = useCallback(
    (couponId: string) => {
      const priceRetail = getChoiceUnitPriceRegular(couponId, visibleRequested);
      setDeliverModal({
        couponIds: [couponId],
        priceRetail,
        priceWhole: '',
        priceRebate: '',
        isBulk: false,
      });
    },
    [visibleRequested],
  );

  const openBulkDeliverModal = useCallback(() => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    const firstId = targetIds[0];
    if (!firstId) return;
    const priceRetail = getChoiceUnitPriceRegular(firstId, visibleRequested);
    setDeliverModal({
      couponIds: targetIds,
      priceRetail,
      priceWhole: '',
      priceRebate: '',
      isBulk: true,
    });
  }, [selectedIds, visibleRequested]);

  const handleConfirmDeliver = useCallback(async () => {
    if (!deliverModal) return;

    const priceWhole = Number(deliverModal.priceWhole);
    const priceRebate = Number(deliverModal.priceRebate);

    if (!deliverModal.priceWhole || isNaN(priceWhole)) {
      alert('도매가를 입력해주세요.');
      return;
    }
    if (!deliverModal.priceRebate || isNaN(priceRebate)) {
      alert('리베이트를 입력해주세요.');
      return;
    }

    const dto: DeliverCouponDto = {
      priceWhole,
      priceRetail: deliverModal.priceRetail,
      priceRebate,
    };

    if (deliverModal.isBulk) {
      setIsBulkProcessing(true);
      setDeliverModal(null);
      let successCount = 0;
      let failCount = 0;

      for (const couponId of deliverModal.couponIds) {
        try {
          await deliverCoupon(couponId, dto);
          successCount++;
        } catch (error) {
          console.error(`발송 처리 실패 (${couponId}):`, error);
          failCount++;
        }
      }

      setIsBulkProcessing(false);
      setSelectedIds(new Set());
      invalidateDashboard();

      if (failCount === 0) {
        alert(`${successCount}건 발송 처리 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    } else {
      const couponId = deliverModal.couponIds[0] ?? '';
      setDeliveringId(couponId);
      setDeliverModal(null);

      try {
        await deliverCoupon(couponId, dto);
        invalidateDashboard();
        alert('발송 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송 처리 실패:', error);
        alert('발송 처리에 실패했습니다.');
      } finally {
        setDeliveringId(null);
      }
    }
  }, [deliverModal, invalidateDashboard]);

  const handleReject = useCallback(
    async (couponId: string) => {
      if (
        !confirm('이 쿠폰을 발송불가 처리하시겠습니까?\n(수신자 번호가 잘못된 경우 등)')
      ) {
        return;
      }
      try {
        await rejectCoupon(couponId);
        invalidateDashboard();
        alert('발송불가 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송불가 처리 실패:', error);
        alert('발송불가 처리에 실패했습니다.');
      }
    },
    [invalidateDashboard],
  );

  const handleUpdateRecipientPhone = useCallback(
    async (couponId: string, newPhone: string) => {
      const cleaned = newPhone.replace(/[^0-9]/g, '');
      if (cleaned.length < 10 || cleaned.length > 11) {
        alert('올바른 전화번호를 입력해주세요.');
        return false;
      }
      try {
        await updateRecipientPhone(couponId, cleaned);
        invalidateDashboard();
        showToast('수신자 번호가 변경되었습니다.');
        return true;
      } catch (error) {
        console.error('수신자 번호 변경 실패:', error);
        alert('수신자 번호 변경에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  const openHistoryModal = useCallback((coupon: GiftCouponForRedemption) => {
    const dc = coupon as GiftCouponForRedemptionDashboard;
    if (dc.phoneHistory && dc.phoneHistory.length > 0) {
      setHistoryModal({
        phone: coupon.recipient?.phoneNumber || '',
        history: dc.phoneHistory,
      });
    }
  }, []);

  const openShareHistoryModal = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.shareHistory && coupon.shareHistory.length > 0) {
      setShareHistoryModal({
        fullCode: coupon.fullCode || coupon._id,
        history: coupon.shareHistory,
      });
    }
  }, []);

  return {
    // Data
    dashboardStatus,
    statistics,
    visibleRequested,
    todayDelivered,
    filteredData,
    productOptions,

    // Filters
    productFilter,
    setProductFilter,
    searchText,
    setSearchText,

    // Selection
    selectedIds,
    isAllSelected,
    handleToggleAll,
    handleToggleSelect,
    setSelectedIds,

    // Deliver
    deliveringId,
    isBulkProcessing,
    openDeliverModal,
    openBulkDeliverModal,

    // Modals
    deliverModal,
    setDeliverModal,
    handleConfirmDeliver,
    handleReject,
    handleUpdateRecipientPhone,
    historyModal,
    setHistoryModal,
    openHistoryModal,
    shareHistoryModal,
    setShareHistoryModal,
    openShareHistoryModal,

    // Today delivered
    showTodayDelivered,
    setShowTodayDelivered,

    // Toast & clipboard
    toastMessage,
    showToast,
    copyToClipboard,
  };
}
